// src/pages/project/settings/ProjectSettingsGeneralPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectAccessCtx } from 'src/features/projects/ProjectAccessContext';
import { hasPerm } from 'src/features/projects/hooks/useProjectAccess';
import { useToast } from 'src/shared/ui/toast/useToast';
import {
    useProjectSetting,
    useUpdateWbsWeekAnchor,
    useUpdateWbsViewPrefs,
    useUpdateWbsEvApprovalPolicy,
} from 'src/features/projects/hooks/useProjectSetting';

const DOWS = [
    { value: 0, label: '일' },
    { value: 1, label: '월' },
    { value: 2, label: '화' },
    { value: 3, label: '수' },
    { value: 4, label: '목' },
    { value: 5, label: '금' },
    { value: 6, label: '토' },
] as const;

function getDowLabel(value: number | null | undefined) {
    return DOWS.find((d) => d.value === value)?.label ?? '-';
}

function formatDateKo(date: Date, timeZone: string, withWeekday = true) {
    return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...(withWeekday ? { weekday: 'long' } : {}),
        timeZone,
    }).format(date);
}

const ProjectSettingsGeneralPage: React.FC = () => {
    const { projectId } = useParams();
    const { access } = useProjectAccessCtx();
    const toast = useToast();

    const { data: settingRes, isLoading, isError } = useProjectSetting(projectId);
    const setting = settingRes?.data ?? null;
    const projectTimeZone = setting?.timeZone ?? 'Asia/Seoul';

    const canWrite =
        // Settings 탭 접근 퍼미션은 이미 쓰고 있으니 유지
        hasPerm(access?.permissions, 'project.settings.open') &&
        // 쓰기는 서버가 role guard로 막을 테지만, UI에서도 최소한 roleName 기반으로 막아줌
        (access?.roleName === 'PROJECT_OWNER' || access?.roleName === 'PROJECT_MANAGER');
    const canChangeWeekAnchor = (setting?.canChangeWeekAnchor ?? true) && canWrite;
    const weekAnchorLockedReason = setting?.weekAnchorLockedReason ?? null;

    const mut = useUpdateWbsWeekAnchor(projectId as string);

    const [anchorDow, setAnchorDow] = useState<number>(5);
    const [applyMode, setApplyMode] = useState<'IMMEDIATE' | 'NEXT_WEEK'>('NEXT_WEEK');
    const [localMsg, setLocalMsg] = useState<string | null>(null);

    const mutApproval = useUpdateWbsEvApprovalPolicy(projectId as string);
    const [wbsEvApproveBeforeClose, setWbsEvApproveBeforeClose] = useState(false);
    const [approvalMsg, setApprovalMsg] = useState<string | null>(null);

    useEffect(() => {
        if (!setting) return;
        setWbsEvApproveBeforeClose(!!setting.wbsEvApproveBeforeClose); // null/undefined면 false(디폴트)
    }, [setting]);

    const onSaveApprovalPolicy = async () => {
        setApprovalMsg(null);
        try {
            await mutApproval.mutateAsync({ wbsEvApproveBeforeClose });
            setApprovalMsg('저장되었습니다.');
        } catch (e: any) {
            setApprovalMsg(e?.response?.data?.error?.message ?? '저장에 실패했습니다.');
        }
    };

    useEffect(() => {
        if (!setting) return;
        setAnchorDow(setting.wbsWeekAnchorDow ?? 5);
    }, [setting]);

    const mutView = useUpdateWbsViewPrefs(projectId as string);

    const [wbsVisibilityMode, setWbsVisibilityMode] = useState<'SHOW_ALL_READONLY' | 'HIDE_UNASSIGNED_FOR_COMPANY'>(
        'SHOW_ALL_READONLY',
    );
    const [companyWbsDefaultView, setCompanyWbsDefaultView] = useState<'ALL' | 'ONLY_ASSIGNED'>('ALL');
    const [viewMsg, setViewMsg] = useState<string | null>(null);

    useEffect(() => {
        if (!setting) return;
        setWbsVisibilityMode((setting.wbsVisibilityMode as any) ?? 'SHOW_ALL_READONLY');
        setCompanyWbsDefaultView((setting.companyWbsDefaultView as any) ?? 'ALL');
    }, [setting]);

    const onSaveViewPrefs = async () => {
        setViewMsg(null);
        try {
            await mutView.mutateAsync({ wbsVisibilityMode, companyWbsDefaultView });
            setViewMsg('저장되었습니다.');
        } catch (e: any) {
            setViewMsg(e?.response?.data?.error?.message ?? '저장에 실패했습니다.');
        }
    };

    const pendingText = useMemo(() => {
        if (setting?.wbsWeekAnchorDowPending == null || !setting?.wbsWeekAnchorEffectiveFrom) return null;
        const p = getDowLabel(setting.wbsWeekAnchorDowPending);
        const dt = new Date(setting.wbsWeekAnchorEffectiveFrom);
        return `예정: ${formatDateKo(dt, projectTimeZone)}부터 ${p}요일 적용`;
    }, [projectTimeZone, setting]);

    const onSave = async () => {
        setLocalMsg(null);
        const previousDow = setting?.wbsWeekAnchorDow ?? 5;
        try {
            const res = await mut.mutateAsync({ anchorDow, applyMode });
            const nextSetting = res.data;
            const fromLabel = getDowLabel(previousDow);
            const toLabel = getDowLabel(anchorDow);

            let message = '';
            if (applyMode === 'IMMEDIATE') {
                message = `${formatDateKo(new Date(), projectTimeZone)} 기준 요일이 '${fromLabel}요일'에서 '${toLabel}요일'로 즉시 변경되었습니다.`;
            } else {
                const effectiveFrom = nextSetting?.wbsWeekAnchorEffectiveFrom
                    ? new Date(nextSetting.wbsWeekAnchorEffectiveFrom)
                    : null;
                message = effectiveFrom
                    ? `${formatDateKo(effectiveFrom, projectTimeZone)}부터 기준 요일이 '${fromLabel}요일'에서 '${toLabel}요일'로 변경됩니다.`
                    : `기준 요일 변경이 저장되었습니다. 다음 기준 주부터 '${toLabel}요일'이 적용됩니다.`;
            }

            setLocalMsg(message);
            toast.success(message, {
                title: '기준 요일 저장 완료',
                duration: 4200,
            });
        } catch (e: any) {
            const msg = e?.response?.data?.error?.message ?? '저장에 실패했습니다.';
            setLocalMsg(msg);
            toast.error(msg, { title: '기준 요일 저장 실패' });
        }
    };

    return (
        <div className="p-4 space-y-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Project Settings</h3>

            {/* ✅ WBS 기준 주 설정 */}
            <div className="rounded-xl border bg-white p-4">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="text-sm font-semibold text-gray-900">WBS 기준 주 (Week Anchor)</div>
                        <div className="mt-1 text-xs text-gray-500">
                            EV/PV 기준 주 계산에 사용되는 “주 시작 요일(Anchor)”을 설정합니다.
                        </div>
                    </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">기준 요일</label>
                        <select
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
                            value={anchorDow}
                            onChange={(e) => setAnchorDow(Number(e.target.value))}
                            disabled={!canChangeWeekAnchor || isLoading}
                        >
                            {DOWS.map((d) => (
                                <option key={d.value} value={d.value}>
                                    {d.label}요일
                                </option>
                            ))}
                        </select>

                        <div className="mt-2 text-xs text-gray-500">
                            현재: {getDowLabel(setting?.wbsWeekAnchorDow ?? 5)}요일
                        </div>

                        {pendingText && <div className="mt-1 text-xs text-amber-700">{pendingText}</div>}
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">적용 방식</label>

                        <div className="space-y-2">
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="applyMode"
                                    value="NEXT_WEEK"
                                    checked={applyMode === 'NEXT_WEEK'}
                                    onChange={() => setApplyMode('NEXT_WEEK')}
                                    disabled={!canChangeWeekAnchor || isLoading}
                                />
                                다음 기준 주부터 적용
                            </label>

                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="radio"
                                    name="applyMode"
                                    value="IMMEDIATE"
                                    checked={applyMode === 'IMMEDIATE'}
                                    onChange={() => setApplyMode('IMMEDIATE')}
                                    disabled={!canChangeWeekAnchor || isLoading}
                                />
                                즉시 적용
                            </label>
                        </div>

                        <div className="mt-2 text-xs text-gray-500">
                            ※ 즉시 적용은 현재 화면에서 보이는 PV/EV 기준도 바로 바뀔 수 있습니다.
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs">
                        {isError ? <span className="text-red-600">설정 정보를 불러오지 못했습니다.</span> : null}
                        {localMsg ? <span className="text-gray-700">{localMsg}</span> : null}
                    </div>

                    <button
                        className="rounded-lg bg-sky-700 text-white px-4 py-1.5 text-xs hover:bg-sky-800 disabled:opacity-50"
                        onClick={onSave}
                        disabled={!canChangeWeekAnchor || isLoading || mut.isPending}
                    >
                        {mut.isPending ? '저장 중...' : '저장'}
                    </button>
                </div>

                {weekAnchorLockedReason && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        {weekAnchorLockedReason}
                    </div>
                )}

                {!canWrite && (
                    <div className="mt-2 text-xs text-gray-500">
                        이 설정은 Project Owner / Project Manager만 변경할 수 있습니다.
                    </div>
                )}
            </div>

            {/* ✅ WBS 표시/기본 보기 설정 */}
            <div className="rounded-xl border bg-white p-4">
                <div className="text-sm font-semibold text-gray-900">WBS 보기 기본값</div>
                <div className="mt-1 text-xs text-gray-500">
                    회사 사용자(WBS 실행사) 화면에서 기본으로 어떤 WBS를 보여줄지, 그리고 미배정 항목을 어떻게 처리할지
                    설정합니다.
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">WBS Visibility Mode</label>
                        <select
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
                            value={wbsVisibilityMode}
                            onChange={(e) => setWbsVisibilityMode(e.target.value as any)}
                            disabled={!canWrite || isLoading}
                        >
                            <option value="SHOW_ALL_READONLY">전체 표시(미배정은 읽기전용)</option>
                            <option value="HIDE_UNASSIGNED_FOR_COMPANY">미배정 항목 숨김(회사 기준)</option>
                        </select>
                        <div className="mt-2 text-xs text-gray-500">
                            ※ “숨김” 모드는 회사 사용자 화면에서 미배정 항목을 노출하지 않습니다.
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs text-gray-500 mb-1">Company WBS Default View</label>
                        <select
                            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm disabled:bg-gray-50"
                            value={companyWbsDefaultView}
                            onChange={(e) => setCompanyWbsDefaultView(e.target.value as any)}
                            disabled={!canWrite || isLoading}
                        >
                            <option value="ALL">전체</option>
                            <option value="ONLY_ASSIGNED">배정된 것만</option>
                        </select>
                        <div className="mt-2 text-xs text-gray-500">
                            ※ 회사 사용자가 WBS 화면에 처음 들어갈 때 적용되는 기본 보기입니다.
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs">{viewMsg ? <span className="text-gray-700">{viewMsg}</span> : null}</div>

                    <button
                        className="rounded-lg bg-sky-700 text-white px-4 py-1.5 text-xs hover:bg-sky-800 disabled:opacity-50"
                        onClick={onSaveViewPrefs}
                        disabled={!canWrite || isLoading || mutView.isPending}
                    >
                        {mutView.isPending ? '저장 중...' : '저장'}
                    </button>
                </div>

                {!canWrite && (
                    <div className="mt-2 text-xs text-gray-500">
                        이 설정은 Project Owner / Project Manager만 변경할 수 있습니다.
                    </div>
                )}
            </div>

            {/* ✅ EV 승인 정책 */}
            <div className="rounded-xl border bg-white p-4">
                <div className="text-sm font-semibold text-gray-900">EV 승인 정책</div>
                <div className="mt-1 text-xs text-gray-500">
                    기본값은 “마감 전 승인 금지”입니다. 필요 시 “언제든 승인 가능”으로 변경할 수 있습니다.
                </div>

                <div className="mt-4 flex items-center justify-between gap-4">
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={wbsEvApproveBeforeClose}
                            onChange={(e) => setWbsEvApproveBeforeClose(e.target.checked)}
                            disabled={!canWrite || isLoading}
                        />
                        마감 전에도 승인 가능(Anytime)
                    </label>

                    <button
                        className="rounded-lg bg-sky-700 text-white px-4 py-1.5 text-xs hover:bg-sky-800 disabled:opacity-50"
                        onClick={onSaveApprovalPolicy}
                        disabled={!canWrite || isLoading || mutApproval.isPending}
                    >
                        {mutApproval.isPending ? '저장 중...' : '저장'}
                    </button>
                </div>

                <div className="mt-2 text-xs">
                    {approvalMsg ? <span className="text-gray-700">{approvalMsg}</span> : null}
                </div>

                {!canWrite && (
                    <div className="mt-2 text-xs text-gray-500">
                        이 설정은 Project Owner / Project Manager만 변경할 수 있습니다.
                    </div>
                )}
            </div>

            {/* 기존 General UI(placeholder) — 필요하면 유지/삭제 */}
            <div className="rounded-xl border bg-white p-4">
                <div className="text-sm font-semibold text-gray-900">기본 정보</div>
                <div className="mt-2 text-xs text-gray-500">
                    (현재 파일은 placeholder입니다. 필요하면 프로젝트 메타 정보 저장 UI로 확장하세요.)
                </div>
            </div>
        </div>
    );
};

export default ProjectSettingsGeneralPage;
